import { timer } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { filter, switchMap, take, exhaustMap } from 'rxjs/operators';
import { AxiosError } from 'axios';
import { LinkButton } from '../../../../../link/LinkButton';
import { JobAction } from '../../../../interfaces/job-action';
import { useActionsHandler } from '../../../../../action-handler/useActionsHandler';
import { ActionStatus, ActionType } from '../../../../../action-handler/action-handler.service';
import { GenericStatusColorType } from '../../../../../shared/interfaces/generic-status';
import { GenericStatusLabel } from '../../../../../shared/components/GenericStatusLabel/GenericStatusLabel';
import { useAnalyticsTracker } from '../../../../../tracking/hooks/useAnalyticsTracker';
import { JobsTrackers } from '../../../../jobs-trackers';
import { getErrorMessageFromAxiosError } from '../../../../../request/utils';
import { JobsAPI } from '../../../../JobsAPI';
import { JobSessionLogStatus } from '../../../../interfaces/job';
import { Tooltip } from '../../../../../tooltip/Tooltip';
import { toasterService } from '../../../../../toaster/toaster.service';
import { downloadLink } from '../../../../../shared/utils/downloadLink';
import { EntityAction } from '../../../../../entity-actions/interfaces/entity-actions';

export function DownloadJobSessionLog({
  pipelineId,
  jobId,
  actionState
}: {
  pipelineId: number;
  jobId: string;
  actionState: EntityAction<JobAction>;
}) {
  const { eventTrack } = useAnalyticsTracker();

  const { status, performAction } = useActionsHandler(
    `download-session-log-${jobId}`,
    ActionType.DOWNLOAD,
    {
      progressTitle: `Preparing Session Log for Download #${jobId}`,
      failureTitle: `Session Log Download failed for #${jobId}`
    }
  );

  const initiateDownload = () => {
    const download$ = fromPromise(JobsAPI.initiateJobSessionLogDownload(pipelineId, jobId)).pipe(
      switchMap(() => {
        return timer(0, 5000);
      }),
      exhaustMap(() => {
        return fromPromise(JobsAPI.getJobDownloadLogStatus(jobId));
      }),
      filter(res =>
        [JobSessionLogStatus.COMPLETED, JobSessionLogStatus.FAILED].includes(res.status)
      ),
      take(1)
    );

    performAction(
      download$
        .toPromise()
        .then(res => {
          if (res.status === JobSessionLogStatus.FAILED) {
            throw new AxiosError(res.message);
          }

          eventTrack({
            action: JobsTrackers.JOB_TRACKER_DOWNLOAD_JOB_SESSION_LOG_SUCCESS,
            properties: {
              jobId
            }
          });

          downloadLink(res.preSignedURL!);
        })
        .catch(error => {
          eventTrack({
            action: JobsTrackers.JOB_TRACKER_DOWNLOAD_JOB_SESSION_LOG_ERROR,
            properties: {
              jobId,
              error: getErrorMessageFromAxiosError(error)
            }
          });

          toasterService.pop('error', undefined, getErrorMessageFromAxiosError(error));

          throw error;
        })
    );
  };

  if (status.status === ActionStatus.IN_PROGRESS) {
    return (
      <GenericStatusLabel
        className='mr-5'
        status={{
          displayName: 'Preparing Session Log for Download',
          icon: 'testing',
          value: null,
          colorType: GenericStatusColorType.INFO
        }}
      />
    );
  }

  if (status.status === ActionStatus.FAILED) {
    return (
      <div className='center-flex-row mr-5'>
        <GenericStatusLabel
          status={{
            displayName: 'Session Log Download Failed.',
            icon: 'error-outline',
            value: null,
            colorType: GenericStatusColorType.ERROR
          }}
        />

        <LinkButton
          dataId='download-job-session-log-retry'
          tag='button'
          className='ml-1'
          onClick={() => {
            eventTrack({
              action: JobsTrackers.JOB_TRACKER_DOWNLOAD_JOB_SESSION_LOG_RETRY_CLICK,
              properties: {
                jobId
              }
            });

            initiateDownload();
          }}
        >
          Retry
        </LinkButton>
      </div>
    );
  }

  return (
    <Tooltip
      dataId='download-job-session-log'
      title={actionState.disabledReason}
      disabled={!actionState.disabled}
    >
      <span>
        <LinkButton
          dataId='download-job-session-log'
          tag='button'
          icon='download'
          direction='left'
          className='mr-5'
          disabled={actionState.disabled}
          onClick={() => {
            eventTrack({
              action: JobsTrackers.JOB_TRACKER_DOWNLOAD_JOB_SESSION_LOG_CLICK,
              properties: {
                jobId
              }
            });

            initiateDownload();
          }}
        >
          Download Session Log
        </LinkButton>
      </span>
    </Tooltip>
  );
}
